import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Stack,
  Input,
  IconButton,
  Button,
  TextField,
  Chip,
  FormHelperText,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useEffect, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';
import Iconify from 'src/components/iconify';
import Image from 'src/components/image';
import {
  getJobDescTextFromDoc,
  getJobDescTextFromLink,
} from 'src/services/role.service';
import { uploadResumeToS3 } from 'src/services/aws.service';
import { RHFEditor } from 'src/components/hook-form';
import { useSnackbar } from 'notistack';
import { useAuthContext } from 'src/auth/hooks';
import { getOrganizationId } from 'src/services/user.service';
import { useJobCreationContext } from 'src/sections/job2/hooks/use-job-creation-context';
import SvgColor from 'src/components/svg-color';
import { useOnClickOutside } from 'usehooks-ts';
import { useBoolean } from 'src/hooks/use-boolean';

const JOB_DESCRIPTION_OPTIONS = {
  MANUAL: 'MANUAL',
  UPLOAD_JD: 'UPLOAD_JD',
  PASTE_JOB_LINK: 'PASTE_JOB_LINK',
  GENERATE_WITH_AI: 'GENERATE_WITH_AI',
};

const FILE_EXTENSION_MAP = {
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    'docx',
  'application/msword': 'doc',
  'application/pdf': 'pdf',
};

const VisuallyHiddenInput = styled('input')`
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  bottom: 0;
  left: 0;
  white-space: nowrap;
  width: 1px;
`;

export default function JobDescription({
  methods,
  handleGenerateWithAi,
  isGeneratingWithAi,
  isGenerateWithAiButtonClicked,
}) {
  // states
  const [jobUrl, setJobUrl] = useState('');
  const [selectedJobDescOption, setSelectedJobDescOption] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [showJobDetails, setShowJobDetails] = useState(false);
  const [addNewSkill, setAddNewSkill] = useState(false);
  const [newSkill, setNewSkill] = useState('');
  const [skillInputWidth, setSkillInputWidth] = useState('68px');
  const isUploadingJD = useBoolean(false);
  const isFetchingFromLink = useBoolean(false);

  // hooks
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuthContext();
  const organizationId = getOrganizationId(user);
  const { canEdit, jobSkills, jobDescription } = useJobCreationContext();

  // refs
  const fileInputRef = useRef(null);
  const addSkillInputRef = useRef(null);

  // functions
  const handleFileChange = event => {
    setSelectedFile(event.target.files[0]);
    console.log(selectedFile);
  };

  const handleRefreshIconClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const fetchDetailsFromLink = async () => {
    isFetchingFromLink.onTrue();
    try {
      const payload = {
        job_url: `${jobUrl}`,
      };

      const response = await getJobDescTextFromLink(payload, organizationId);

      if (response.success) {
        methods.setValue(
          'description',
          `${response.body.job_description}`.replaceAll('\n', '<br>'),
        );
        methods.setValue('skills', response.body.skills);

        setShowJobDetails(true);
      } else throw new Error(response.message);
    } catch (err) {
      console.log(err);
    } finally {
      isFetchingFromLink.onFalse();
    }
  };

  const fetchDetailsFromFile = async () => {
    isUploadingJD.onTrue();
    const fileExtension = FILE_EXTENSION_MAP[selectedFile.type];
    const accept = selectedFile.type;
    // const fileUrl = await uploadJobDescriptionToS3(
    //   selectedFile,
    //   fileExtension,
    //   accept,
    // );
    try {
      const fileUrl = await uploadResumeToS3(
        selectedFile,
        'job-descriptions',
        accept,
        fileExtension,
      );

      const payload = {
        document_url: fileUrl,
      };

      const response = await getJobDescTextFromDoc(payload);

      if (response.success) {
        methods.setValue(
          'description',
          `${response.body || ''}`.replaceAll('\n', '<br>'),
        );

        setShowJobDetails(true);
      } else throw new Error(response.message);
    } catch (error) {
      enqueueSnackbar({
        variant: 'error',
        message: 'Something went wrong',
      });
      console.log(error);
    } finally {
      isUploadingJD.onFalse();
    }
  };

  const handleAddNewSkill = () => {
    if (newSkill) {
      methods.setValue('skills', [...skills, newSkill]);
    }
    setNewSkill('');
    setAddNewSkill(false);
    methods.trigger();
  };

  const handleSkillInputChange = event => {
    const inputValue = event.target.value;
    setNewSkill(inputValue);

    const newWidth = `${Math.max(24, inputValue.length * 8)}px`;
    setSkillInputWidth(newWidth);
  };

  const handleEnterKeyPress = event => {
    if (event.key === 'Enter') {
      event.preventDefault();

      const inputValue = event.target.value;
      setNewSkill(inputValue);

      handleAddNewSkill();
    }
  };

  // effects
  useEffect(() => {
    if (jobDescription) {
      methods.setValue('description', jobDescription);
      methods.setValue('skills', jobSkills);
      setShowJobDetails(true);
    }
  }, []);

  const skills = methods.getValues('skills');

  useOnClickOutside(addSkillInputRef, handleAddNewSkill);

  return !showJobDetails ? (
    <Box
      sx={{
        background: theme => theme.palette.common.white,
        px: 3,
        py: 4,
        border: theme => `1px solid ${theme.palette.border.primaryBlack2}`,
        borderRadius: '6px',
      }}
    >
      <Stack spacing={3}>
        <Typography variant="subheading1">
          How do you want to fill the Job Details
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1.5} width="100%">
          <Button
            variant="outlined"
            sx={{
              width: '100%',
              whiteSpace: 'nowrap',
              borderColor: theme =>
                selectedJobDescOption === JOB_DESCRIPTION_OPTIONS.MANUAL &&
                theme.palette.border.active,
            }}
            startIcon={<Iconify icon="tabler:edit" />}
            onClick={() => {
              setShowJobDetails(true);
            }}
          >
            Manual
          </Button>
          <Button
            component="label"
            variant="outlined"
            sx={{
              width: '100%',
              whiteSpace: 'nowrap',
              borderColor: theme =>
                selectedJobDescOption === JOB_DESCRIPTION_OPTIONS.UPLOAD_JD &&
                theme.palette.border.active,
            }}
            startIcon={<Iconify icon="tabler:upload" />}
            onClick={() => {
              setSelectedJobDescOption('UPLOAD_JD');
            }}
          >
            Upload JD
            <VisuallyHiddenInput
              type="file"
              accept=".pdf, .doc, .docx"
              ref={fileInputRef}
              onChange={handleFileChange}
            />
          </Button>
          {/* <Button
            variant="outlined"
            sx={{
              width: '220px',
              whiteSpace: 'nowrap',
              borderColor: theme =>
                selectedJobDescOption ===
                  JOB_DESCRIPTION_OPTIONS.PASTE_JOB_LINK &&
                theme.palette.border.active,
            }}
            startIcon={<Iconify icon="tabler:link" />}
            onClick={() => {
              setSelectedJobDescOption('PASTE_JOB_LINK');
            }}
          >
            Paste Job link
          </Button> */}
          <LoadingButton
            loading={isGeneratingWithAi}
            loadingPosition="start"
            variant="contained"
            sx={{
              width: '100%',
              whiteSpace: 'nowrap',
              color: theme => theme.palette.content.primaryBlack1,
              background: theme => theme.palette.background.primaryGreen3,
              '&:hover': {
                background: theme => theme.palette.background.primaryBlack1,
                color: theme => theme.palette.common.white,
              },
            }}
            startIcon={<SvgColor src="/assets/icons/job/ic_star_twinkle.svg" />}
            onClick={async () => {
              await handleGenerateWithAi();
              setShowJobDetails(true);
            }}
          >
            {isGeneratingWithAi ? 'Generating with AI' : 'Generate with AI'}
          </LoadingButton>
        </Stack>
        {selectedJobDescOption === JOB_DESCRIPTION_OPTIONS.MANUAL && (
          <Stack spacing={2}>
            <RHFEditor simple name="description" />
            <Button
              sx={{
                width: 'min-content',
              }}
              variant="contained"
              onClick={() => {
                setShowJobDetails(true);
              }}
            >
              Continue
            </Button>
          </Stack>
        )}
        {selectedJobDescOption === JOB_DESCRIPTION_OPTIONS.UPLOAD_JD &&
          selectedFile && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ width: '100%' }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ width: '100%' }}
                justifyContent="space-between"
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Image
                    src="/assets/icons/job/ic_job_desc_file_upload.svg"
                    alt="file_upload"
                  />
                  <Typography>{selectedFile.name}</Typography>
                </Stack>
                <Stack direction="row" alignItems="center">
                  <IconButton onClick={handleRefreshIconClick}>
                    <Iconify icon="tabler:refresh" />
                  </IconButton>
                  <IconButton
                    onClick={() => {
                      setSelectedFile(null);
                      setSelectedJobDescOption('');
                    }}
                  >
                    <Iconify icon="tabler:x" />
                  </IconButton>
                </Stack>
              </Stack>
              <LoadingButton
                loading={isUploadingJD.value}
                variant="contained"
                onClick={fetchDetailsFromFile}
                sx={{ whiteSpace: 'nowrap', px: 3, py: 2 }}
              >
                Fetch Details
              </LoadingButton>
            </Stack>
          )}
        {selectedJobDescOption === JOB_DESCRIPTION_OPTIONS.PASTE_JOB_LINK && (
          <Stack direction="row" alignItems="center" spacing={1}>
            <TextField
              fullWidth
              placeholder="Eg. https://hireintel.app/jobpostinglink-software-engineeri..."
              value={jobUrl}
              onChange={e => setJobUrl(e.target.value)}
            />
            <LoadingButton
              loading={isFetchingFromLink.value}
              variant="contained"
              sx={{ whiteSpace: 'nowrap', px: 3, py: 2 }}
              onClick={fetchDetailsFromLink}
            >
              Fetch Details
            </LoadingButton>
          </Stack>
        )}
      </Stack>
    </Box>
  ) : (
    <>
      <Stack spacing={3}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ width: '100%' }}
        >
          <Typography variant="subheading1">Job Description</Typography>
          {isGenerateWithAiButtonClicked ? null : (
            <LoadingButton
              loading={isGeneratingWithAi}
              loadingPosition="start"
              variant="contained"
              sx={{
                width: '170px',
                whiteSpace: 'nowrap',
                color: theme => theme.palette.content.primaryBlack1,
                background: theme => theme.palette.background.primaryGreen3,
                '&:hover': {
                  background: theme => theme.palette.background.primaryBlack1,
                  color: theme => theme.palette.common.white,
                },
              }}
              startIcon={
                <SvgColor src="/assets/icons/job/ic_star_twinkle.svg" />
              }
              onClick={async () => {
                await handleGenerateWithAi();
                setShowJobDetails(true);
              }}
            >
              {isGeneratingWithAi ? 'Generating with AI' : 'Generate with AI'}
            </LoadingButton>
          )}
        </Stack>
        <RHFEditor name="description" simple />
      </Stack>
      <Stack spacing={3}>
        <Typography variant="subheading1">Skills</Typography>
        <Stack>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            flexWrap="wrap"
          >
            <Chip
              label={
                addNewSkill ? (
                  <Input
                    ref={addSkillInputRef}
                    value={newSkill}
                    disabled={!canEdit}
                    autoFocus
                    onChange={handleSkillInputChange}
                    onKeyDown={handleEnterKeyPress}
                    disableUnderline
                    style={{
                      background: 'transparent',
                      padding: 0,
                      margin: 0,
                      minWidth: '68px',
                      width: skillInputWidth,
                    }}
                  />
                ) : (
                  'Add skill'
                )
              }
              variant="outlined"
              clickable={!addNewSkill}
              onClick={() => {
                if (!addNewSkill) setAddNewSkill(true);
              }}
              sx={{
                background: theme =>
                  addNewSkill && theme.palette.background.primaryAccent2,
                '&:hover': {
                  background: theme => theme.palette.background.primaryAccent2,
                },
              }}
              disabled={!canEdit}
              icon={!addNewSkill && <Iconify icon="tabler:plus" />}
            />
            {skills.map(skill => (
              <Chip
                key={skill}
                label={skill}
                clickable
                variant="outlined"
                disabled={!canEdit}
                onDelete={() => {
                  const _skills = skills.filter(_skill => _skill !== skill);

                  methods.setValue('skills', _skills);
                }}
              />
            ))}
          </Stack>
          {methods.formState.errors?.skills && (
            <FormHelperText
              error={!!methods.formState.errors.skills}
              sx={{ px: 2 }}
            >
              {methods.formState.errors.skills.message}
            </FormHelperText>
          )}
        </Stack>
      </Stack>
    </>
  );
}

JobDescription.propTypes = {
  methods: PropTypes.object,
  handleGenerateWithAi: PropTypes.func,
  isGeneratingWithAi: PropTypes.bool,
  isGenerateWithAiButtonClicked: PropTypes.bool,
};
